(() => {
  'use strict';

  if (window.__UNTCHBL_REVIEW_SYSTEM_V3__) return;
  window.__UNTCHBL_REVIEW_SYSTEM_V3__ = true;

  const summaryCache = new Map();

  const STAR_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279-7.416-3.967-7.417 3.967 1.481-8.279-6.064-5.828 8.332-1.151z"></path>' +
    '</svg>';

  const parseBoolean = (value) => value === 'true';

  function cleanBaseUrl(value) {
    return String(value || '')
      .trim()
      .replace(/\/+$/, '');
  }

  function getConfig(root) {
    return {
      url: cleanBaseUrl(root.dataset.supabaseUrl),
      key: String(root.dataset.supabaseKey || '').trim(),
      shopDomain: String(root.dataset.shopDomain || '')
        .trim()
        .toLowerCase(),
      includeDemo: parseBoolean(root.dataset.includeDemo),
      showHoodieLabel: parseBoolean(root.dataset.showHoodieLabel),
      showDemoBadge: parseBoolean(root.dataset.showDemoBadge)
    };
  }

  function hasBackend(config) {
    if (!config.key || !config.shopDomain) return false;

    try {
      const url = new URL(config.url);

      return (
        url.protocol === 'https:' &&
        Boolean(url.hostname)
      );
    } catch (_) {
      return false;
    }
  }

  async function rpc(config, functionName, payload) {
    if (!hasBackend(config)) {
      throw new Error(
        'Das Bewertungs-Backend ist noch nicht vollständig konfiguriert.'
      );
    }

    const response = await fetch(
      `${config.url}/rest/v1/rpc/${functionName}`,
      {
        method: 'POST',
        headers: {
          apikey: config.key,
          Authorization: `Bearer ${config.key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      let message =
        'Die Anfrage an das Bewertungs-Backend ist fehlgeschlagen.';
      let code = '';

      try {
        const error = await response.json();

        message =
          error.message ||
          error.error_description ||
          message;

        code = error.code || '';
      } catch (_) {
        // Allgemeine Fehlermeldung verwenden,
        // wenn Supabase kein JSON zurückgibt.
      }

      const exception = new Error(message);
      exception.code = code;

      throw exception;
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  function missingRpc(error) {
    const message = String(error?.message || '');

    return (
      error?.code === 'PGRST202' ||
      /could not find the function|schema cache/i.test(message)
    );
  }

  function summaryKey(config) {
    return [
      config.url,
      config.shopDomain,
      String(config.includeDemo)
    ].join('|');
  }

  function normaliseSummary(row) {
    const count = Number(row?.review_count || 0);

    const distribution = {
      5: Number(row?.five_star_count || 0),
      4: Number(row?.four_star_count || 0),
      3: Number(row?.three_star_count || 0),
      2: Number(row?.two_star_count || 0),
      1: Number(row?.one_star_count || 0)
    };

    return {
      average: Number(row?.average_rating || 0),
      count,
      fiveStarPercent: Number(row?.five_star_percent || 0),
      verifiedCount: Number(row?.verified_count || 0),
      distribution
    };
  }

  async function loadSummary(config, force = false) {
    const key = summaryKey(config);

    if (!force && summaryCache.has(key)) {
      return summaryCache.get(key);
    }

    const request = rpc(
      config,
      'untchbl_get_shop_review_dashboard',
      {
        p_shop_domain: config.shopDomain,
        p_include_demo: config.includeDemo
      }
    )
      .then((rows) => {
        const row = Array.isArray(rows)
          ? rows[0]
          : rows;

        return normaliseSummary(row);
      })
      .catch((error) => {
        summaryCache.delete(key);
        throw error;
      });

    summaryCache.set(key, request);

    return request;
  }

  function renderStars(container, rating) {
    if (!container) return;

    const safeRating = Math.max(
      0,
      Math.min(5, Number(rating) || 0)
    );

    container.innerHTML = '';

    container.setAttribute(
      'aria-label',
      `${safeRating.toFixed(1)} von 5 Sternen`
    );

    for (let index = 1; index <= 5; index += 1) {
      const fill =
        Math.max(
          0,
          Math.min(
            1,
            safeRating - (index - 1)
          )
        ) * 100;

      const star = document.createElement('span');
      star.className = 'untchbl-rating-star';

      star.innerHTML = `
        <span class="untchbl-rating-star__base">
          ${STAR_SVG}
        </span>

        <span
          class="untchbl-rating-star__fill"
          style="width:${fill}%"
        >
          ${STAR_SVG}
        </span>
      `;

      container.appendChild(star);
    }
  }

  function formatCount(count) {
    return new Intl.NumberFormat('de-DE').format(
      Number(count) || 0
    );
  }

  function formatRelativeDate(value) {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const seconds = Math.round(
      (date.getTime() - Date.now()) / 1000
    );

    const formatter = new Intl.RelativeTimeFormat(
      'de',
      {
        numeric: 'auto'
      }
    );

    if (Math.abs(seconds) < 60) {
      return formatter.format(seconds, 'second');
    }

    const minutes = Math.round(seconds / 60);

    if (Math.abs(minutes) < 60) {
      return formatter.format(minutes, 'minute');
    }

    const hours = Math.round(minutes / 60);

    if (Math.abs(hours) < 24) {
      return formatter.format(hours, 'hour');
    }

    const days = Math.round(hours / 24);

    if (Math.abs(days) < 30) {
      return formatter.format(days, 'day');
    }

    const months = Math.round(days / 30);

    if (Math.abs(months) < 12) {
      return formatter.format(months, 'month');
    }

    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  function updateSummaryElement(root, summary) {
    root
      .querySelectorAll('[data-rating-value]')
      .forEach((element) => {
        element.textContent =
          summary.count > 0
            ? summary.average.toFixed(1)
            : '0.0';
      });

    root
      .querySelectorAll('[data-review-count]')
      .forEach((element) => {
        element.textContent = formatCount(summary.count);
      });

    root
      .querySelectorAll('[data-five-star-percent]')
      .forEach((element) => {
        element.textContent = String(
          Math.round(summary.fiveStarPercent)
        );
      });

    root
      .querySelectorAll('[data-rating-stars]')
      .forEach((element) => {
        renderStars(
          element,
          summary.average
        );
      });

    for (
      let rating = 1;
      rating <= 5;
      rating += 1
    ) {
      const count = Number(
        summary.distribution?.[rating] || 0
      );

      const percent =
        summary.count > 0
          ? Math.round(
              (count / summary.count) * 100
            )
          : 0;

      root
        .querySelectorAll(
          `[data-rating-bar="${rating}"]`
        )
        .forEach((element) => {
          element.style.width = `${percent}%`;
        });

      root
        .querySelectorAll(
          `[data-rating-percent="${rating}"]`
        )
        .forEach((element) => {
          element.textContent = `${percent}%`;
        });
    }

    root.classList.toggle(
      'is-empty',
      summary.count === 0
    );

    if (
      parseBoolean(
        root.dataset.hideWhenEmpty
      )
    ) {
      root.hidden = summary.count === 0;
    }
  }

  async function initSummary(root) {
    const config = getConfig(root);

    const fallbackCount = Number(
      root.dataset.fallbackCount || 0
    );

    const fallbackPercent = Number(
      root.dataset.fallbackFiveStarPercent || 0
    );

    const fallback = {
      average: Number(
        root.dataset.fallbackRating || 0
      ),
      count: fallbackCount,
      fiveStarPercent: fallbackPercent,
      verifiedCount: 0,
      distribution: {
        5: Math.round(
          fallbackCount *
            (fallbackPercent / 100)
        ),
        4: 0,
        3: 0,
        2: 0,
        1: 0
      }
    };

    if (!hasBackend(config)) {
      updateSummaryElement(root, fallback);
      root.dataset.backendState = 'missing';

      return fallback;
    }

    try {
      const summary = await loadSummary(config);

      updateSummaryElement(root, summary);
      root.dataset.backendState = 'ready';

      return summary;
    } catch (error) {
      console.error(
        '[UNTCHBL Reviews] Zusammenfassung konnte nicht geladen werden:',
        error
      );

      updateSummaryElement(root, fallback);
      root.dataset.backendState = 'error';

      return fallback;
    }
  }

  function reviewerInitials(name) {
    const words = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!words.length) {
      return '?';
    }

    if (words.length === 1) {
      return words[0]
        .slice(0, 2)
        .toUpperCase();
    }

    return (
      `${words[0][0]}${words[words.length - 1][0]}`
    ).toUpperCase();
  }

  function createReviewElement(
    review,
    config
  ) {
    const article = document.createElement(
      'article'
    );

    article.className =
      'untchbl-review-item';

    const meta = document.createElement(
      'div'
    );

    meta.className =
      'untchbl-review-item__meta';

    const avatar = document.createElement(
      'span'
    );

    avatar.className =
      'untchbl-review-avatar';

    avatar.textContent = reviewerInitials(
      review.reviewer_name
    );

    avatar.setAttribute(
      'aria-hidden',
      'true'
    );

    const author = document.createElement(
      'div'
    );

    author.className =
      'untchbl-review-author';

    const authorLine =
      document.createElement('div');

    authorLine.className =
      'untchbl-review-author__line';

    const name = document.createElement(
      'strong'
    );

    name.className =
      'untchbl-review-author__name';

    name.textContent = String(
      review.reviewer_name || 'Anonym'
    );

    authorLine.appendChild(name);

    if (review.verified_purchase) {
      const verified =
        document.createElement('span');

      verified.className =
        'untchbl-review-verified';

      verified.textContent =
        '✓ Verifiziert';

      authorLine.appendChild(verified);
    }

    if (
      review.is_demo &&
      config.showDemoBadge
    ) {
      const demo =
        document.createElement('span');

      demo.className =
        'untchbl-review-demo-badge';

      demo.textContent = 'Demo';

      authorLine.appendChild(demo);
    }

    const date = document.createElement(
      'time'
    );

    date.className =
      'untchbl-review-item__date';

    date.dateTime = String(
      review.created_at || ''
    );

    date.textContent = formatRelativeDate(
      review.created_at
    );

    author.append(
      authorLine,
      date
    );

    meta.append(
      avatar,
      author
    );

    const stars = document.createElement(
      'div'
    );

    stars.className =
      'untchbl-rating-stars ' +
      'untchbl-rating-stars--small ' +
      'untchbl-review-item__stars';

    renderStars(
      stars,
      Number(review.rating)
    );

    article.append(
      meta,
      stars
    );

    const categoryName = String(
      review.hoodie_name || ''
    ).trim();

    if (
      config.showHoodieLabel &&
      categoryName
    ) {
      const categoryTag =
        document.createElement('span');

      categoryTag.className =
        'untchbl-review-product-tag';

      categoryTag.textContent =
        categoryName;

      article.appendChild(categoryTag);
    }

    if (review.title) {
      const title =
        document.createElement('h3');

      title.className =
        'untchbl-review-item__title';

      title.textContent = review.title;

      article.appendChild(title);
    }

    const body = document.createElement(
      'p'
    );

    body.className =
      'untchbl-review-item__body';

    body.textContent = String(
      review.body || ''
    );

    article.appendChild(body);

    return article;
  }

  function setMessage(
    element,
    message,
    type = ''
  ) {
    if (!element) return;

    element.textContent = message;

    element.classList.remove(
      'is-success',
      'is-error',
      'is-info'
    );

    if (type) {
      element.classList.add(
        `is-${type}`
      );
    }

    element.hidden = !message;
  }

  function readableSubmitError(error) {
    const message = String(
      error?.message || ''
    );

    if (
      message.includes(
        'review_recently_submitted'
      )
    ) {
      return (
        'Für diese E-Mail-Adresse wurde ' +
        'innerhalb der letzten 24 Stunden ' +
        'bereits eine Bewertung eingereicht.'
      );
    }

    if (
      message.includes('invalid_email')
    ) {
      return (
        'Bitte gib eine gültige ' +
        'E-Mail-Adresse ein.'
      );
    }

    if (
      message.includes(
        'invalid_hoodie_name'
      )
    ) {
      return (
        'Bitte wähle eine Kategorie aus.'
      );
    }

    if (
      message.includes('invalid_')
    ) {
      return (
        'Bitte prüfe deine Eingaben ' +
        'und versuche es erneut.'
      );
    }

    return (
      'Die Bewertung konnte nicht gespeichert werden. ' +
      'Bitte versuche es später erneut.'
    );
  }

  function getCooldownKey(config) {
    return (
      'untchbl-shop-review-cooldown:' +
      config.shopDomain
    );
  }

  function isCoolingDown(config) {
    try {
      const value = Number(
        localStorage.getItem(
          getCooldownKey(config)
        ) || 0
      );

      return (
        Date.now() - value <
        60_000
      );
    } catch (_) {
      return false;
    }
  }

  function setCooldown(config) {
    try {
      localStorage.setItem(
        getCooldownKey(config),
        String(Date.now())
      );
    } catch (_) {
      // Die Datenbankvalidierung schützt
      // den Endpunkt weiterhin.
    }
  }

  async function fetchReviews(
    config,
    activeRating,
    activeCategory,
    pageSize,
    offset
  ) {
    return rpc(
      config,
      'untchbl_get_shop_reviews',
      {
        p_shop_domain:
          config.shopDomain,

        p_rating:
          activeRating,

        p_hoodie_name:
          activeCategory || null,

        p_limit:
          pageSize,

        p_offset:
          offset,

        p_include_demo:
          config.includeDemo
      }
    );
  }

  function initReviewSection(root) {
    const config = getConfig(root);

    const list = root.querySelector(
      '[data-reviews-list]'
    );

    const emptyState = root.querySelector(
      '[data-reviews-empty]'
    );

    const loadMoreButton =
      root.querySelector(
        '[data-load-more]'
      );

    const formContainer =
      root.querySelector(
        '[data-review-form-container]'
      );

    const openFormButton =
      root.querySelector(
        '[data-open-review-form]'
      );

    const closeFormButtons =
      root.querySelectorAll(
        '[data-close-review-form]'
      );

    const form = root.querySelector(
      '[data-review-form]'
    );

    const formMessage =
      root.querySelector(
        '[data-form-message]'
      );

    const listMessage =
      root.querySelector(
        '[data-list-message]'
      );

    const submitButton =
      form?.querySelector(
        '[type="submit"]'
      );

    const filterButtons =
      root.querySelectorAll(
        '[data-rating-filter]'
      );

    const categoryFilter =
      root.querySelector(
        '[data-hoodie-filter]'
      );

    const pageSize = Math.max(
      1,
      Math.min(
        20,
        Number(root.dataset.pageSize) ||
          6
      )
    );

    let offset = 0;
    let loading = false;
    let activeRating = null;
    let activeCategory = '';

    const setFormOpen = (open) => {
      if (
        !formContainer ||
        !openFormButton
      ) {
        return;
      }

      formContainer.hidden = !open;

      openFormButton.setAttribute(
        'aria-expanded',
        String(open)
      );

      if (open) {
        formContainer
          .querySelector(
            'input, select, textarea'
          )
          ?.focus();

        formContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    };

    openFormButton?.addEventListener(
      'click',
      () => setFormOpen(true)
    );

    closeFormButtons.forEach(
      (button) => {
        button.addEventListener(
          'click',
          () => {
            setFormOpen(false);
            openFormButton?.focus();
          }
        );
      }
    );

    async function loadReviews(
      reset = false
    ) {
      if (loading || !list) return;

      if (!hasBackend(config)) {
        setMessage(
          listMessage,
          'Trage im Theme-Editor die Supabase-URL und den Publishable Key ein.',
          'info'
        );

        loadMoreButton?.setAttribute(
          'hidden',
          ''
        );

        emptyState?.setAttribute(
          'hidden',
          ''
        );

        return;
      }

      loading = true;

      loadMoreButton?.setAttribute(
        'aria-busy',
        'true'
      );

      if (reset) {
        setMessage(
          listMessage,
          'Bewertungen werden geladen …',
          'info'
        );
      }

      try {
        const reviews =
          await fetchReviews(
            config,
            activeRating,
            activeCategory,
            pageSize,
            reset ? 0 : offset
          );

        if (reset) {
          list.innerHTML = '';
          offset = 0;
        }

        const rows = Array.isArray(
          reviews
        )
          ? reviews
          : [];

        rows.forEach((review) => {
          list.appendChild(
            createReviewElement(
              review,
              config
            )
          );
        });

        offset += rows.length;

        const hasReviews =
          list.children.length > 0;

        if (emptyState) {
          emptyState.hidden =
            hasReviews;
        }

        if (loadMoreButton) {
          loadMoreButton.hidden =
            rows.length < pageSize;
        }

        setMessage(
          listMessage,
          '',
          ''
        );
      } catch (error) {
        console.error(
          '[UNTCHBL Reviews] Bewertungen konnten nicht geladen werden:',
          error
        );

        const migrationHint =
          missingRpc(error)
            ? ' Führe zuerst supabase/migration-v3.sql aus.'
            : '';

        setMessage(
          listMessage,
          `Bewertungen konnten momentan nicht geladen werden.${migrationHint}`,
          'error'
        );
      } finally {
        loading = false;

        loadMoreButton?.removeAttribute(
          'aria-busy'
        );
      }
    }

    filterButtons.forEach(
      (button) => {
        button.addEventListener(
          'click',
          () => {
            const value =
              button.dataset.ratingFilter;

            activeRating =
              value === 'all'
                ? null
                : Number(value);

            filterButtons.forEach(
              (item) => {
                const active =
                  item === button;

                item.classList.toggle(
                  'is-active',
                  active
                );

                item.setAttribute(
                  'aria-pressed',
                  String(active)
                );
              }
            );

            loadReviews(true);
          }
        );
      }
    );

    categoryFilter?.addEventListener(
      'change',
      () => {
        activeCategory =
          categoryFilter.value;

        loadReviews(true);
      }
    );

    loadMoreButton?.addEventListener(
      'click',
      () => loadReviews(false)
    );

    form?.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();

        setMessage(
          formMessage,
          '',
          ''
        );

        if (!hasBackend(config)) {
          setMessage(
            formMessage,
            'Das Bewertungs-Backend ist noch nicht konfiguriert.',
            'error'
          );

          return;
        }

        if (isCoolingDown(config)) {
          setMessage(
            formMessage,
            'Bitte warte kurz, bevor du das Formular erneut absendest.',
            'error'
          );

          return;
        }

        const formData =
          new FormData(form);

        const categoryName = String(
          formData.get('hoodie_name') ||
            ''
        ).trim();

        if (!categoryName) {
          setMessage(
            formMessage,
            'Bitte wähle eine Kategorie aus.',
            'error'
          );

          return;
        }

        const originalButtonText =
          submitButton?.textContent ||
          'Bewertung absenden';

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent =
            'Wird gespeichert …';
        }

        try {
          await rpc(
            config,
            'untchbl_submit_shop_review',
            {
              p_shop_domain:
                config.shopDomain,

              p_hoodie_name:
                categoryName,

              p_reviewer_name:
                String(
                  formData.get(
                    'reviewer_name'
                  ) || ''
                ),

              p_reviewer_email:
                String(
                  formData.get(
                    'reviewer_email'
                  ) || ''
                ),

              p_rating:
                Number(
                  formData.get(
                    'rating'
                  )
                ),

              p_title:
                String(
                  formData.get(
                    'title'
                  ) || ''
                ),

              p_body:
                String(
                  formData.get(
                    'body'
                  ) || ''
                ),

              p_website:
                String(
                  formData.get(
                    'website'
                  ) || ''
                )
            }
          );

          setCooldown(config);
          form.reset();

          setMessage(
            formMessage,
            'Danke. Deine Shop-Bewertung wurde gespeichert und wird nach der Freigabe veröffentlicht.',
            'success'
          );
        } catch (error) {
          console.error(
            '[UNTCHBL Reviews] Bewertung konnte nicht gespeichert werden:',
            error
          );

          setMessage(
            formMessage,
            readableSubmitError(error),
            'error'
          );
        } finally {
          if (submitButton) {
            submitButton.disabled = false;

            submitButton.textContent =
              originalButtonText;
          }
        }
      }
    );

    initSummary(root);
    loadReviews(true);
  }

  function scan(scope = document) {
    scope
      .querySelectorAll(
        '[data-untchbl-summary-root]:not([data-untchbl-initialized])'
      )
      .forEach((root) => {
        root.dataset.untchblInitialized =
          'true';

        initSummary(root);
      });

    scope
      .querySelectorAll(
        '[data-untchbl-reviews-root]:not([data-untchbl-initialized])'
      )
      .forEach((root) => {
        root.dataset.untchblInitialized =
          'true';

        initReviewSection(root);
      });
  }

  scan();

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => scan(),
      {
        once: true
      }
    );
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      scan(event.target);
    }
  );
})();